"use client";

import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { gridMetrics } from "@/components/hero/HeroGrid";
import { useRefGridMode } from "./RefGridContext";

/**
 * Row-snapper for one-row floaters (CTA pills, tab rows, logo rows) in grid
 * mode. On md+ it grows to a whole number of grid rows, shifts its top edge
 * down onto the next grid row line, and vertically centers its (single) child
 * — so the content sits centered *in* a row instead of straddling a line, per
 * the grid-paper principle ("blocks span whole rows; content is vertically
 * centered within the row"). Emits `grid-relayout` so GridClearing and the
 * decoration field re-measure after the shift. Outside grid mode / below md
 * it's a plain div.
 */
export default function GridRowCenter({
  className = "",
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  const onGrid = useRefGridMode();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!onGrid) return;
    const el = ref.current;
    if (!el) return;

    const compute = () => {
      const w = document.documentElement.clientWidth;
      const prev = { h: el.style.height, m: el.style.marginTop };
      el.style.height = "";
      el.style.marginTop = "";
      if (w < 768) {
        if (prev.h || prev.m) window.dispatchEvent(new Event("grid-relayout"));
        return;
      }
      const { cell } = gridMetrics(w);
      const content = el.firstElementChild as HTMLElement | null;
      const h = content?.offsetHeight ?? el.offsetHeight;
      const rows = Math.max(1, Math.ceil(h / cell));
      el.style.height = `${rows * cell}px`;
      const top = el.getBoundingClientRect().top + window.scrollY;
      const snap = (Math.ceil(top / cell) * cell - top) % cell;
      el.style.marginTop = `${snap}px`;
      // Only broadcast when something actually moved — listeners recompute on
      // this event and an unconditional dispatch would ping-pong forever.
      if (prev.h !== el.style.height || prev.m !== el.style.marginTop) {
        window.dispatchEvent(new Event("grid-relayout"));
      }
    };

    compute();
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    fonts?.ready.then(compute).catch(() => {});

    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    };
    window.addEventListener("resize", schedule);
    window.addEventListener("grid-relayout", schedule);
    return () => {
      window.removeEventListener("resize", schedule);
      window.removeEventListener("grid-relayout", schedule);
      cancelAnimationFrame(raf);
    };
  }, [onGrid]);

  return (
    <div
      ref={ref}
      className={`${onGrid ? "md:flex md:items-center md:justify-center" : ""} ${className}`}
    >
      {children}
    </div>
  );
}
